import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search, MapPin, Hotel, UtensilsCrossed, Sparkles, ShoppingBag,
  Calendar, Users, Loader2, ArrowRight, ImageIcon, Minus, Plus, X,
  Castle, CalendarHeart,
} from 'lucide-react';

// ------------------------------------------------------------------ helpers
const norm = (s) =>
  (s == null ? '' : String(s)).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const euro = (n) =>
  n == null ? '' : `€${Number(n || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (iso, days) => {
  const d = iso ? new Date(iso) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// I mondi nativi selezionabili nel finder.
const TYPES = [
  { key: 'hotel', label: 'Soggiorni', icon: Hotel, catalog: '/alloggi', noun: 'strutture', noun1: 'struttura' },
  { key: 'experience', label: 'Esperienze', icon: Sparkles, catalog: '/esperienze/catalogo', noun: 'esperienze', noun1: 'esperienza' },
  { key: 'tour', label: 'Tour Borghi', icon: Castle, catalog: '/tour-nei-borghi', noun: 'tour', noun1: 'tour' },
  { key: 'eventi', label: 'Eventi', icon: CalendarHeart, catalog: '/eventi', noun: 'eventi', noun1: 'evento' },
  { key: 'restaurant', label: 'Ristoranti', icon: UtensilsCrossed, catalog: '/dove-mangiare', noun: 'ristoranti', noun1: 'ristorante' },
  { key: 'shop', label: 'Shop', icon: ShoppingBag, catalog: '/shop', noun: 'negozi', noun1: 'negozio' },
];

// Normalizza ogni dataset in un formato comune per suggerimenti + conteggi.
function normalizeDatasets(raw) {
  const hotels = (raw.hotel || []).map((s) => ({
    id: s.owner_id, name: s.name, sub: s.address || s.tagline || '',
    img: s.cover_image, price: s.from_price, to: `/struttura/${s.owner_id}`,
    hay: norm([s.name, s.address, s.tagline].join(' ')),
  }));
  const restaurants = (raw.restaurant || []).map((s) => ({
    id: s.owner_id, name: s.name, sub: s.address || s.tagline || '',
    img: s.cover_image, price: s.from_price, to: `/ristorante/${s.owner_id}`,
    hay: norm([s.name, s.address, s.tagline].join(' ')),
  }));
  const experiences = ((raw.experience && raw.experience.experiences) || []).map((e) => ({
    id: e.id, name: e.title, sub: (e.location && e.location.name) || e.category || '',
    img: e.cover_image, price: e.from_price, to: `/esperienze/${e.slug || e.id}`,
    hay: norm([e.title, e.location && e.location.name, e.location && e.location.address, e.category, e.owner_name].join(' ')),
  }));
  const shops = (raw.shop || []).map((s) => ({
    id: s.owner_id, name: s.name, sub: (s.categories || []).slice(0, 3).join(', ') || s.tagline || '',
    img: s.cover_image, price: s.from_price, to: `/negozio/${s.owner_id}`,
    hay: norm([s.name, s.tagline, (s.categories || []).join(' ')].join(' ')),
  }));
  // Tour nei Borghi: itinerari (experience type=tour) → dettaglio esperienza.
  const tours = ((raw.tour && raw.tour.experiences) || []).map((e) => ({
    id: e.id, name: e.title, sub: (e.location && e.location.name) || e.category || '',
    img: e.cover_image, price: e.from_price, to: `/esperienze/${e.slug || e.id}`,
    hay: norm([e.title, e.location && e.location.name, e.location && e.location.address, e.category, e.short_desc, e.owner_name].join(' ')),
  }));
  // Eventi: festival/sagre/eventi speciali → pagina evento pubblica.
  const eventi = ((raw.eventi && raw.eventi.events) || []).map((e) => ({
    id: e.id, name: e.title, sub: e.location || e.category || '',
    img: e.cover, price: e.price_from, to: `/eventi/${e.slug || e.id}`,
    hay: norm([e.title, e.location, e.location_address, e.category, e.short, e.owner_name].join(' ')),
  }));
  return { hotel: hotels, restaurant: restaurants, experience: experiences, shop: shops, tour: tours, eventi };
}

function Segmented({ type, setType }) {
  return (
    <div className="grid grid-cols-3 gap-1 rounded-2xl bg-muted/70 p-1" role="tablist" data-testid="finder-type-tabs">
      {TYPES.map((t) => {
        const Icon = t.icon;
        const active = type === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setType(t.key)}
            data-testid={`finder-type-${t.key}`}
            className={cn(
              'flex flex-col items-center gap-1 rounded-xl px-1 py-2 text-[11px] font-semibold transition-colors duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              active
                ? 'bg-background text-primary shadow-[var(--shadow-sm)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function Stepper({ value, setValue, min = 1, max = 20, label, id }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium">{label}</label>
      <div className="flex items-center justify-between rounded-md border border-input bg-background px-2 py-1.5">
        <button
          type="button"
          aria-label="Diminuisci"
          onClick={() => setValue(Math.max(min, value - 1))}
          disabled={value <= min}
          data-testid={`${id}-minus`}
          className="flex h-7 w-7 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent disabled:opacity-40"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="min-w-[2ch] text-center text-sm font-semibold" data-testid={`${id}-value`}>{value}</span>
        <button
          type="button"
          aria-label="Aumenta"
          onClick={() => setValue(Math.min(max, value + 1))}
          disabled={value >= max}
          data-testid={`${id}-plus`}
          className="flex h-7 w-7 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function ExperienceFinder({ title = 'Componi la tua vacanza' }) {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState({ hotel: [], restaurant: [], experience: [], shop: [], tour: [], eventi: [] });
  const [loading, setLoading] = useState(true);

  const [type, setType] = useState('hotel');
  const [where, setWhere] = useState('');
  const [openSuggest, setOpenSuggest] = useState(false);

  // Soggiorni / Ristoranti / Esperienze: parametri "componi vacanza"
  const [checkin, setCheckin] = useState(todayISO());
  const [checkout, setCheckout] = useState(plusDaysISO(todayISO(), 2));
  const [guests, setGuests] = useState(2);
  const [expDate, setExpDate] = useState('');
  const [party, setParty] = useState(2);

  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);

  // Carica una volta i 4 dataset nativi (in parallelo, tollerante ai fallimenti).
  useEffect(() => {
    let alive = true;
    (async () => {
      const results = await Promise.allSettled([
        api.get('/hotel/public/structures'),
        api.get('/restaurant/public/structures'),
        api.get('/experience/public/experiences'),
        api.get('/ecommerce/public/shops'),
        api.get('/experience/public/experiences', { params: { type: 'tour' } }),
        api.get('/events/public'),
      ]);
      if (!alive) return;
      const val = (r) => (r.status === 'fulfilled' ? r.value.data : null);
      setDatasets(normalizeDatasets({
        hotel: val(results[0]) || [],
        restaurant: val(results[1]) || [],
        experience: val(results[2]) || { experiences: [] },
        shop: val(results[3]) || [],
        tour: val(results[4]) || { experiences: [] },
        eventi: val(results[5]) || { events: [] },
      }));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  // Chiudi il dropdown suggerimenti cliccando fuori (considera anche il menu in portal).
  useEffect(() => {
    const onDown = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inWrap && !inMenu) setOpenSuggest(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Il dropdown è renderizzato in un PORTAL (document.body) per uscire da qualsiasi
  // stacking-context (es. il motion.div animato della home) ed essere sempre cliccabile.
  useEffect(() => {
    if (!openSuggest) return undefined;
    const update = () => {
      if (wrapRef.current) setAnchorRect(wrapRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [openSuggest, type, where]);

  const q = norm(where);

  const matches = useMemo(() => {
    const pool = datasets[type] || [];
    return q ? pool.filter((x) => x.hay.includes(q)) : pool;
  }, [datasets, type, q]);
  const suggestions = useMemo(() => matches.slice(0, 6), [matches]);
  const meta = TYPES.find((t) => t.key === type);
  const count = matches.length;

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (where.trim()) params.set('loc', where.trim());
    if (type === 'hotel') {
      if (checkin) params.set('checkin', checkin);
      if (checkout) params.set('checkout', checkout);
      if (guests) params.set('guests', String(guests));
    } else if (type === 'restaurant') {
      if (expDate) params.set('date', expDate);
      if (party) params.set('party', String(party));
    } else if (type === 'experience') {
      if (expDate) params.set('date', expDate);
    }
    const qs = params.toString();
    navigate(qs ? `${meta.catalog}?${qs}` : meta.catalog);
  };

  // Deep-link a una singola entità dai suggerimenti. Per gli hotel porta con sé
  // date/ospiti così la vetrina pre-compila e lancia la disponibilità reale.
  const suggestionHref = (s) => {
    if (type === 'hotel') {
      const p = new URLSearchParams();
      if (checkin) p.set('checkin', checkin);
      if (checkout) p.set('checkout', checkout);
      if (guests) p.set('adults', String(guests));
      const qs = p.toString();
      return qs ? `${s.to}?${qs}` : s.to;
    }
    return s.to;
  };

  return (
    <div
      className="grain-overlay rounded-[24px] border border-white/20 bg-background/90 p-5 text-foreground shadow-[0_20px_70px_rgba(0,0,0,0.35)] backdrop-blur-xl sm:p-6"
      data-testid="home-hero-finder-card"
    >
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/12 text-primary">
          <Sparkles className="h-4 w-4" />
        </span>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
      </div>

      <Segmented type={type} setType={(k) => { setType(k); setOpenSuggest(false); }} />

      {/* Destinazione con autocomplete reale */}
      <div className="relative mt-3" ref={wrapRef}>
        <label className="mb-1.5 block text-sm font-medium">{(type === 'eventi' || type === 'tour') ? 'Cosa cerchi' : 'Dove'}</label>
        <div className="relative">
          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9 pr-9"
            aria-label="Dove"
            placeholder={
              type === 'eventi' ? 'Cerca un evento, festival, sagra…'
                : type === 'tour' ? 'Cerca un tour o un borgo…'
                : 'Sila Grande, Camigliatello, nome…'
            }
            value={where}
            onChange={(e) => { setWhere(e.target.value); setOpenSuggest(true); }}
            onFocus={() => setOpenSuggest(true)}
            data-testid="home-finder-where"
          />
          {where && (
            <button
              type="button"
              aria-label="Cancella"
              onClick={() => { setWhere(''); setOpenSuggest(false); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {openSuggest && anchorRect && createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', left: anchorRect.left, top: anchorRect.bottom + 8, width: anchorRect.width, zIndex: 2000 }}
            className="overflow-hidden rounded-2xl border border-border bg-popover shadow-[0_20px_60px_rgba(0,0,0,0.28)]"
            data-testid="finder-suggestions"
          >
            {loading ? (
              <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Carico le destinazioni…
              </div>
            ) : suggestions.length === 0 ? (
              <div className="px-4 py-4 text-sm text-muted-foreground" data-testid="finder-suggest-empty">
                Nessun risultato per “{where}”. Prova un altro nome o premi Cerca.
              </div>
            ) : (
              <ul className="max-h-72 overflow-auto py-1">
                {suggestions.map((s) => (
                  <li key={`${type}-${s.id}`}>
                    <button
                      type="button"
                      onClick={() => { setOpenSuggest(false); navigate(suggestionHref(s)); }}
                      data-testid={`finder-suggest-${s.id}`}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
                        {s.img ? (
                          <img src={mediaUrl(s.img)} alt="" className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-foreground">{s.name}</span>
                        {s.sub && <span className="block truncate text-xs text-muted-foreground">{s.sub}</span>}
                      </span>
                      {s.price != null && (
                        <span className="shrink-0 text-xs font-semibold text-primary">da {euro(s.price)}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body,
        )}
      </div>

      {/* Parametri "componi la tua vacanza" per tipo */}
      {type === 'hotel' && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Check-in</label>
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input type="date" min={todayISO()} value={checkin}
                onChange={(e) => { setCheckin(e.target.value); if (e.target.value >= checkout) setCheckout(plusDaysISO(e.target.value, 1)); }}
                className="pl-9" data-testid="home-finder-checkin" aria-label="Check-in" />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Check-out</label>
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input type="date" min={plusDaysISO(checkin, 1)} value={checkout}
                onChange={(e) => setCheckout(e.target.value)}
                className="pl-9" data-testid="home-finder-checkout" aria-label="Check-out" />
            </div>
          </div>
          <div className="col-span-2">
            <Stepper id="home-finder-guests" label="Ospiti" value={guests} setValue={setGuests} min={1} max={16} />
          </div>
        </div>
      )}

      {type === 'restaurant' && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Quando</label>
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input type="date" min={todayISO()} value={expDate}
                onChange={(e) => setExpDate(e.target.value)} className="pl-9" data-testid="home-finder-rdate" aria-label="Quando (data prenotazione)" />
            </div>
          </div>
          <Stepper id="home-finder-party" label="Coperti" value={party} setValue={setParty} min={1} max={20} />
        </div>
      )}

      {type === 'experience' && (
        <div className="mt-3">
          <label className="mb-1.5 block text-sm font-medium">Quando (facoltativo)</label>
          <div className="relative">
            <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input type="date" min={todayISO()} value={expDate}
              onChange={(e) => setExpDate(e.target.value)} className="pl-9" data-testid="home-finder-edate" aria-label="Quando (facoltativo)" />
          </div>
        </div>
      )}

      {/* Conteggio risultati live (pre-ricerca reattiva) */}
      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="finder-live-count">
          {loading ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Carico…</>
          ) : (
            <>
              <span className="inline-flex h-2 w-2 rounded-full bg-primary" aria-hidden />
              <strong className="text-foreground">{count}</strong>
              {count === 1 ? ` ${meta.noun1}` : ` ${meta.noun}`}
              {q ? ' per la tua ricerca' : ' disponibili'}
            </>
          )}
        </span>
      </div>

      <Button className="mt-3 w-full rounded-full" size="lg" onClick={handleSearch} data-testid="home-finder-submit">
        <Search className="mr-2 h-4 w-4" />
        Cerca
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>

      <button
        type="button"
        onClick={() => navigate('/vacanza')}
        data-testid="home-finder-trip-link"
        className="mt-2.5 flex w-full items-center justify-center gap-1.5 text-xs font-semibold text-primary transition-colors hover:underline"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Oppure componi l'intera vacanza (soggiorno + esperienze)
      </button>
    </div>
  );
}

export default ExperienceFinder;
